/**
 * PageLodContext — the current page's level-of-detail, provided by PageNode
 * around its grid subtree so a SectionWidget (rendered across the grid's render
 * boundary via renderWidget) knows whether to draw its real nested grid or the
 * cheap far-zoom wireframe.
 *
 * The LOD bands (see usePageNode): `edit` (near 1:1, fully editable), `mockup`
 * (real page, read-only), `wireframe` (zoomed far out). Historically the whole
 * page body was replaced by a static SVG skeleton at `wireframe` — which meant
 * you couldn't reorder or drag-add sections there. Now the page grid stays
 * MOUNTED at every band (so sections reorder + drag-add previews work at any
 * zoom); only the section CONTENT drops to a wireframe SVG at `wireframe`, which
 * keeps the far-zoom perf win (no Lexical/images/nested grids mounted).
 *
 * Defaults to `edit` outside a provider (PreviewCanvas etc. render at 1:1).
 */
import { createContext, useContext, type ReactNode } from "react";

export type PageLod = "wireframe" | "mockup" | "edit";

const PageLodContext = createContext<PageLod>("edit");

export function PageLodProvider({
  value,
  children,
}: {
  value: PageLod;
  children: ReactNode;
}) {
  return (
    <PageLodContext.Provider value={value}>{children}</PageLodContext.Provider>
  );
}

export function usePageLod(): PageLod {
  return useContext(PageLodContext);
}
